import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

/**
 * Admin "Add user" dialog.
 * Generates a temp password server-side and emails it (or surfaces it in dev).
 */
export const AddUserDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "buyer",
    country: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setForm({ email: "", name: "", role: "buyer", country: "" });
    setResult(null);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setResult(data);
      onCreated?.(data.user);
      toast.success(
        data.email_sent
          ? "Account created · invite email sent"
          : "Account created"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none border border-[#E5E0D8] bg-white max-w-lg" data-testid="add-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#111] font-light">
            {result ? "Account created" : "Add a new user"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#555]">
            {result
              ? "A temporary password was generated. The user must change it after first sign-in."
              : "Creates a verified, pre-approved account and emails the user a temporary password."}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-6 mt-2">
            <div>
              <label className="label-eyebrow text-[#555]">Full name</label>
              <Input
                data-testid="add-user-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
              />
            </div>
            <div>
              <label className="label-eyebrow text-[#555]">Email</label>
              <Input
                data-testid="add-user-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-eyebrow text-[#555]">Role</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger
                    data-testid="add-user-role"
                    className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="buyer">Collector / Buyer</SelectItem>
                    <SelectItem value="artist">Artist</SelectItem>
                    <SelectItem value="writer">Journalist / Writer</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-eyebrow text-[#555]">Country</label>
                <Input
                  data-testid="add-user-country"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                className="rounded-none border-[#E5E0D8] text-[#555] hover:text-[#111] px-5 py-3 h-auto label-eyebrow"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="add-user-submit"
                disabled={submitting}
                className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-3 h-auto label-eyebrow"
              >
                {submitting ? "Creating…" : "Create account"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-5 mt-2" data-testid="add-user-result">
            <div className="border-l-2 border-[#F4C430] bg-[#F4EFE6]/40 px-4 py-3">
              <p className="label-eyebrow text-[#1E3A8A]">Invited</p>
              <p className="font-serif-display text-xl text-[#111] mt-1">
                {result.user.name}
              </p>
              <p className="font-mono-aam text-sm text-[#555] mt-1">{result.user.email}</p>
              <p className="text-xs text-[#555] mt-2">
                Role: <span className="label-eyebrow text-[#1E3A8A]">{result.user.role}</span>
              </p>
            </div>
            {result.temp_password && (
              <div className="border border-[#E5E0D8] bg-white p-4">
                <p className="label-eyebrow text-[#555]">Temporary password</p>
                <p
                  data-testid="add-user-temp-password"
                  className="font-mono-aam text-xl text-[#1E3A8A] mt-2 break-all select-all"
                >
                  {result.temp_password}
                </p>
                <p className="text-xs text-[#555] mt-3">
                  {result.email_sent
                    ? "Also emailed to the user. Share this verbally if they don't receive the email."
                    : "Email sending isn't configured — share this password with the user manually."}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={() => handleClose(false)}
                data-testid="add-user-done"
                className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-3 h-auto label-eyebrow w-full"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
